import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { colors } from "../../../../ui/colors";

export interface PaymentProcessingViewProps {
  /** True after PROCESSING_TIMEOUT_SEC without confirmation */
  timedOut?: boolean;
  /** True when invoice fetch failed (e.g. network error) */
  error?: boolean;
  /** Callback to retry/refresh (refetch invoice and leave processing state) */
  onRetry?: () => void;
}

export const PaymentProcessingView = ({ timedOut, error, onRetry }: PaymentProcessingViewProps = {}) => {
  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          py: 4,
          px: 2,
          bgcolor: colors.gray[50],
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: colors.gray[800], textAlign: "center", maxWidth: 360 }}>
          We could not verify your payment status.
        </Typography>
        <Typography variant="body2" sx={{ color: colors.gray[600], textAlign: "center", maxWidth: 360 }}>
          Please check your connection and try again. If the amount was charged, the invoice may already be updated.
        </Typography>
        {onRetry && (
          <Button variant="contained" onClick={onRetry} sx={{ bgcolor: colors.orange.base, "&:hover": { bgcolor: colors.orange[700] } }}>
            Try again
          </Button>
        )}
      </Box>
    );
  }

  if (timedOut) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          py: 4,
          px: 2,
          bgcolor: colors.gray[50],
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: colors.gray[800], textAlign: "center", maxWidth: 360 }}>
          This is taking longer than usual.
        </Typography>
        <Typography variant="body2" sx={{ color: colors.gray[600], textAlign: "center", maxWidth: 360 }}>
          If your payment was successful, refresh the page to see the updated invoice. If the amount was charged and you still do not see confirmation, contact support.
        </Typography>
        {onRetry && (
          <Button variant="contained" onClick={onRetry} sx={{ bgcolor: colors.orange.base, "&:hover": { bgcolor: colors.orange[700] } }}>
            Refresh status
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        py: 4,
        px: 2,
        bgcolor: colors.gray[50],
      }}
    >
      <CircularProgress size={48} sx={{ color: colors.orange.base }} />
      <Typography variant="h6" sx={{ fontWeight: 600, color: colors.gray[800], textAlign: "center", maxWidth: 360 }}>
        Your payment is being processed. Please wait.
      </Typography>
      <Typography variant="body2" sx={{ color: colors.gray[600], textAlign: "center" }}>
        This usually takes a few seconds. Do not close this page.
      </Typography>
    </Box>
  );
};
